/**
 * Phase 15 / LESSB-02 / D-08 — Pre-Confirm-Modal wiring verification.
 * PanelCardPaymentData.tsx uses PossibleBookingType (Pattern S-09, Phase 12 artifact) —
 * the Pre-Confirm-Modal is reachable for Lesson-Bookings without new code.
 * Visual verification: VALIDATION.md row 15-02-04 (Manual-Only).
 *
 * Phase 15 / LESSB-05 / D-13 — RefundPanel Lesson-Booking-Integration (verify-only, Option B).
 * Das Phase-14 `RefundPanel` (src/client/views/RefundPanel/RefundPanel.tsx) wird hier
 * NICHT erneut importiert oder gerendert. Der Lesson-Storno-Flow konsumiert dasselbe
 * Panel transparent via:
 *
 *   SettledLessonBookingDetails.tsx
 *     -> <MoreMenuSettlementDetails booking refetch />  (Zeile ~233)
 *         -> ReverseBookingModal (BookingList/ReverseBookingModal.tsx)
 *             -> <RefundPanel mode="storno" scheduledPayments={mollieRefundableSps} />
 *
 * ReverseBookingModal lädt `scheduledPayments` via useGetBookingByIdQuery(record.id)
 * und filtert refund-fähige Mollie-SPs (Mollie + bezahlt + refundedAmount < paymentAmount).
 * Da die Query booking-type-agnostisch ist, funktioniert sie für Standard- wie
 * Lesson-Bookings unverändert — D-13 "keine LessonRefundPanel-Duplikation" ist erfüllt.
 *
 * Analoger Umbuchen-Flow läuft über TransferAttendeeToAnotherCourse.tsx mit
 * `mode="umbuchen"` und derselben Panel-Komponente (Phase 14 Plan 14-04).
 *
 * Daraus folgt: SettledLessonBookingDetails.tsx benötigt KEINEN eigenen RefundPanel-
 * Import. Jeder zusätzliche Integrator wäre Duplikation (D-15). Visual verification
 * erfolgt durch den bestehenden Storno-Action-Flow im MoreMenuSettlementDetails.
 */
import React, {FC, memo, useEffect, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {Button, ButtonProps, Space, Tag} from "antd";
import moment from "moment";
import {grey, red} from "@ant-design/colors";
import {Icon} from "@ant-design/compatible";
import Page from "../../../../views/Page/Page";
import PageContentWrapper from "../../../../views/PageContentWrapper/PageContentWrapper";
import LayoutStep from "../../../../views/LayoutStep/LayoutStep";
import i18n from "../../../../services/i18n";
import {GetBookingByIdWithCourseLessonBookingsQuery, useGetBookingByIdWithCourseLessonBookingsQuery} from "../../../../generated/graphql";
import PanelCardPaymentData from "../../../../views/PanelCardPaymentData/PanelCardPaymentData";
import PanelCardBooker from "../../../../views/PanelCardBooker/PanelCardBooker";
import RenderInvoicePreview from "../../../../views/RenderInvoicePreview/RenderInvoicePreview";
import PanelCard from "../../../../views/PanelCard/PanelCard";
import {LocationState} from "../../../../types";
import {returnBackHelper} from "../../../../helpers/returnBackHelper";
import {isBookingSettled} from "../../../../helpers/bookingDetailsRouting";
import {bookingHasMolliePayment} from "../../../../helpers/bookingHasMolliePayment";
import {URL_BOOKINGS_LIST, URL_INVOICE_LIST_BY_BOOKING_SETTLED} from "../../../../models/url";
import PanelCardNotes from "../../../../views/PanelCardNotes/PanelCardNotes";
import PanelCardPayments from "../../../../views/PanelCardPayments/PanelCardPayments";
import CancelSummary from "../../../../views/PanelCardPayments/CancelSummary";
// Phase 25 / LB-02 — Strategy-Switch greift auch für echte Lesson-Bookings.
// MolliePaymentSection ist nach Phase-23 D-09 booking-type-agnostisch und wird
// hier für ALLE Mollie-Kurse gerendert (Standard- und Lesson-Bookings).
// Die ursprüngliche Phase-23-Hotfix-Filterklausel (Booking-Typ-Einschränkung
// auf Standard-Pfad) entfällt mit Phase 25.
import {MolliePaymentSection} from "../../../invoice/SettlementDetails/MolliePaymentSection";
import {isActiveState} from "../../../invoice/SettlementDetails/MolliePaymentSection/helpers/isActiveState";
import SendMailMenu from "./SendMailMenu/SendMailMenu";
import {LeftMenuBookingIcon} from "../../../../assets/SVG-Icons";
import CorrectionInvoice from "./CorrectionInvoice/CorrectionInvoice";
import CorrectBookingWizard from "../../../invoice/SettlementDetails/CorrectBookingWizard/CorrectBookingWizard";
import CourseDetailsOverview from "../../../courses/CourseDetailsLessonBooking/Panels/CourseDetailsOverview";
import LessonBookingCourseLessonTableSettled from "../../../../views/LessonBookingCourseLessonTableSettled/LessonBookingCourseLessonTableSettled";
import css from "./SettledLessonBookingDetails.less";
import AnnotationCoursePanel from "./Panels/AnnotationCoursePanel";
import BookingHistoryDrawer from "../../../bookingHistory/BookingHistoryDrawer";
import MoreMenuSettlementDetails from "../../../invoice/SettlementDetails/MoreMenuSettlementDetails";
import AdjustLessonBookingButton from "../../../invoice/SettlementDetails/AdjustLessonBookingButton";
import InvoiceSentStatusTagContainer from "../../../invoice/SettlementDetails/InvoiceSentStatusTagContainer";
import RenderCourseLessons from "../../../../views/PanelCardCourseData/RenderCourseLessons";
import CommunicationHistoryBooking from "../../CommunicationHistoryBooking/CommunicationHistoryBooking";
import LateEntryPricingNotice from "../../../../views/LateEntryPricingNotice/LateEntryPricingNotice";
import ErasedRecordNotice from "../../../../views/ErasedRecordNotice/ErasedRecordNotice";
import useHighestRole, {isAdminOrHigher} from "../../../../helpers/useHighestRole";
import {getDisplayedPaymentMethodType} from "../../../../helpers/getDisplayedPaymentMethodType";
import BookingDunningsPanel from "../../../invoice/SettlementDetails/BookingDunnings/BookingDunningsPanel";
import BookingTarif595Panel from "../../../invoice/SettlementDetails/Tarif595Receipts/BookingTarif595Panel";
import BookingFeeLettersPanel from "../../../invoice/SettlementDetails/FeeLetters/BookingFeeLettersPanel";
import BookingBillingAddressPanel from "../../BookingBillingAddress/BookingBillingAddressPanel";

type SettledLessonBookingDetailsProps = {
    bookingId?: string;
    renderWithoutPage?: boolean;
    modalStyle?: React.CSSProperties;
    setModalOpen?: (value: boolean) => void;
};

const SettledLessonBookingDetails: FC<SettledLessonBookingDetailsProps> = ({bookingId, renderWithoutPage, modalStyle, setModalOpen}) => {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const [booking, setBooking] = useState<GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"] | undefined>(undefined);
    const [shownLessonCount, setShownLessonCount] = useState<number>(0);
    const [paymentMethodTyepIsEsr, setPaymentMethodTypeIsEsr] = useState<boolean>(false);
    const [timeStamp, setTimeStamp] = useState<number>(0);
    const highestRole = useHighestRole();
    // Phase 25 / LB-02 — Mollie-Polling-Interval (30s) bei aktivem Mollie-Status
    // (open/pending/authorized). 0 = kein Polling (klassisch oder final).
    // Greift auch für echte Lesson-Bookings (Filter-Klausel aus Phase 23 entfällt).
    const MOLLIE_POLL_INTERVAL = 30000;
    const [computedPollInterval, setComputedPollInterval] = useState<number | undefined>(0);
    const {data, loading, refetch} = useGetBookingByIdWithCourseLessonBookingsQuery({
        skip: !bookingId,
        variables: {id: bookingId ?? ""},
        fetchPolicy: "network-only",
        pollInterval: computedPollInterval,
    });

    const returnBack = (): void => {
        returnBackHelper({
            history,
            location,
            fallbackUrl: URL_BOOKINGS_LIST,
        });
    };

    const bookingData: GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"] | undefined = data?.bookingByIdWithCourseLessonBookings;

    useEffect(() => {
        setBooking(bookingData);
    }, [bookingData]);

    // Phase 25 / Plan 25-07 / WR-04 — Optional chain auf das courseLessons-Array
    // verhindert NPE bei leerem Array (z.B. transiente Race-Condition zwischen
    // einer Mutation, die alle Lessons austauscht, und dem Re-Read des Bookings).
    // Standard-Pfad SettlementDetails.tsx nutzt dieses Pattern bereits konsequent.
    // Downstream sind keine zusätzlichen Guards nötig: die `renderContent`-JSX
    // ist bereits durch `booking && course ? (...) : null` geschützt (s.u.),
    // und der MolliePaymentSection-Strategy-Switch (`course?.allowMolliePayment === true`)
    // evaluiert für undefined zu false → klassischer PanelCardPayments-Fallback rendert.
    const course = booking?.courseLessons?.[0]?.course;
    const paymentMethodType = booking?.paymentMethod ? getDisplayedPaymentMethodType(booking.paymentMethod) : undefined;

    useEffect(() => {
        if (paymentMethodType === "esr") {
            setPaymentMethodTypeIsEsr(true);
        }
    }, [paymentMethodType]);

    // Phase 25 / LB-02-D3 — Conditional Mollie-Polling. Greift bei ALLEN Bookings
    // mit `allowMolliePayment=true` UND aktivem Mollie-Status (open/pending/authorized),
    // d.h. Standard- UND echte Lesson-Bookings. Der Phase-23-Hotfix-Filter
    // (Booking-Typ-Einschränkung auf Standard-Pfad) entfällt.
    useEffect(() => {
        // Baustein 5 (mollie-treatment-follows-method): folgt der METHODE (SP-Provider) statt Kurs.
        const isMollieBooking = bookingHasMolliePayment(booking?.scheduledPayments);

        if (!isMollieBooking) {
            setComputedPollInterval(0);

            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const attempts = ((booking as any)?.molliePaymentAttempts as Array<{status?: string; isCurrentAttempt?: boolean}> | undefined) ?? [];
        // Mehrere Raten → mehrere aktuelle Attempts: gepollt wird, sobald IRGENDEINER
        // davon aktiv ist (gleiche Regel wie der Polling-Hint in MolliePaymentSection).
        const active = attempts.some((a) => a.isCurrentAttempt === true && isActiveState(a.status));

        setComputedPollInterval(active ? MOLLIE_POLL_INTERVAL : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        booking?.scheduledPayments,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (booking as any)?.molliePaymentAttempts,
    ]);

    const getInvoiceData = (booking: GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"]) => {
        if (!booking.invoice) {
            return undefined;
        }
        if (booking.reversalInvoice) {
            const {invoiceNumberPrefix, invoiceNumber, id} = booking.reversalInvoice;

            return {
                id,
                invoiceNumber,
                invoiceNumberPrefix,
                isReversalInvoice: true,
            };
        }

        const {invoiceNumberPrefix, invoiceNumber, id} = booking.invoice;

        return {
            id,
            invoiceNumber,
            invoiceNumberPrefix,
            isReversalInvoice: false,
        };
    };

    // COLLECTING DATA ---------------------------------------------------------

    const invoiceData = booking ? getInvoiceData(booking) : undefined;

    type BookingType = "PlaceReservation" | "Booking" | "Waitlist";
    const bookingType: BookingType = booking?.bookingType ? (booking.bookingType as BookingType) : "Booking";

    // RETURN ------------------------------------------------------------------

    const isNotSettled = !isBookingSettled(booking);
    const isCanceled = booking?.reversalInvoice?.id !== undefined;
    const subTextSecondPart = isNotSettled ? "" : isCanceled ? `(${i18n.containers.invoice.InvoiceDetails.canceled()})` : `(${i18n.containers.invoice.InvoiceDetails.settled()})`;

    const InvoiceHeader = () => {
        const {correctionDate, createDate} = booking?.invoice ?? {
            correctionDate: undefined,
            createDate: undefined,
        };

        const subStyle: React.CSSProperties = {
            fontSize: "0.8rem",
            fontWeight: 400,
            color: grey[3],
            marginRight: -5,
        };

        const sentStatusTag =
            booking?.id && booking?.invoice ? (
                <InvoiceSentStatusTagContainer
                    bookingId={booking.id}
                    invoice={booking.invoice}
                    isReversal={isCanceled}
                />
            ) : null;

        if (correctionDate) {
            return (
                <Space size={20}>
                    <span>{i18n.containers.invoice.InvoiceDetails.invoice()}</span>
                    <Space size={10} style={subStyle}>
                        <span>{`Erstellt:`}</span>
                        <Tag color="green">{moment(createDate).format("L")}</Tag>
                        <span style={subStyle}>{`Korrektur:`}</span>
                        <Tag color="volcano">{moment(correctionDate).format("L")}</Tag>
                        {sentStatusTag}
                    </Space>
                </Space>
            );
        }

        return (
            <Space size={20}>
                <span>{i18n.containers.invoice.InvoiceDetails.invoice()}</span>
                <Space size={10} style={subStyle}>
                    <span>{`Erstellt`}</span>
                    <Tag color="green">{moment(createDate).format("L")}</Tag>
                    {sentStatusTag}
                </Space>
            </Space>
        );
    };

    const renderContent = () => {
        // Phase 25 / LB-02-D1 — Strategy-Switch (BDSEC-01). Greift jetzt bei ALLEN
        // Mollie-Kursen (Standard- UND echte Lesson-Bookings). Die ursprüngliche
        // Phase-23-Hotfix-Klausel (Booking-Typ-Filter auf Standard-Pfad) entfällt
        // mit Phase 25 — MolliePaymentSection ist nach Phase-23 D-09 booking-type-agnostisch.
        // Baustein 5 (mollie-treatment-follows-method): folgt der METHODE (SP-Provider) statt Kurs.
        const isMollieBooking = bookingHasMolliePayment(booking?.scheduledPayments);

        return (
            <>
                {booking && course ? (
                    <div className={css.contentContainer}>
                        <ErasedRecordNotice erasedAt={booking.attendee?.erasedAt} />
                        <LateEntryPricingNotice
                            pricingReason={booking.pricingReason}
                            pricingCalculatedAt={booking.pricingCalculatedAt}
                            pricingSnapshot={booking.pricingSnapshot}
                        />
                        <CourseDetailsOverview course={course} lessonCount={booking.courseLessons.length} gapAfter />
                        <div className={css.twoColumns}>
                            <LessonBookingCourseLessonTableSettled
                                booking={booking}
                                className={css.panelCard}
                                setShownLessonCount={setShownLessonCount}
                                allowLessonBooking={Boolean(course.allowLessonBooking)}
                                footer={<AdjustLessonBookingButton booking={booking} refetch={refetch} />}
                            />
                            <AnnotationCoursePanel booking={booking} shownLessonCount={shownLessonCount} gapAfter className={css.panelCard} />
                        </div>
                        <div className={paymentMethodType !== "sepaDebit" ? css.twoColumnsResponsive : undefined}>
                            <PanelCardBooker className={css.panelCard} attendee={booking.attendee} />
                            <PanelCardPaymentData
                                className={css.panelCard}
                                booking={booking}
                                refetchScheduledPayments={async () => setTimeStamp(moment.unix(Date.now()).valueOf())}
                                isCanceled={isCanceled}
                                // BDSEC-06 — Mollie-Mode-Variante (hellblau, Logo, kein Edit-Button)
                                mollieMode={isMollieBooking}
                            />
                        </div>
                        <BookingBillingAddressPanel bookingId={booking.id} settled />
                        {isMollieBooking ? (
                            <>
                                {/* Schulen mit Mollie sehen statt der Zahlungskarte die Mollie-Ansicht; das Storno-Ergebnis gehört in beide (Review #1797) */}
                                <div style={{marginBottom: 16}}>
                                    <CancelSummary booking={booking} scheduledPayments={booking.scheduledPayments ?? []} isCanceled={isCanceled} />
                                </div>
                                <MolliePaymentSection
                                    bookingId={booking.id}
                                    scheduledPayments={booking.scheduledPayments ?? []}
                                    molliePaymentAttempts={
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        ((booking as any)?.molliePaymentAttempts ?? [])
                                    }
                                    isCanceled={isCanceled}
                                    isWaitlistBooking={booking.bookingType === "Waitlist"}
                                    refetchBooking={refetch}
                                    // Empfängerzeile im Sammel-Link-Modal.
                                    bookerEmail={booking.attendee?.booker?.email}
                                    canManagePayments={isAdminOrHigher(highestRole)}
                                    attendeeErased={Boolean(booking.attendee?.erasedAt)}
                                />
                            </>
                        ) : (
                            <PanelCardPayments
                                bookingId={booking.id}
                                booking={booking}
                                scheduledPayments={booking.scheduledPayments}
                                isCanceled={isCanceled}
                                refetchBooking={refetch}
                                timeStamp={timeStamp}
                                setTimeStamp={setTimeStamp}
                            />
                        )}
                        <BookingDunningsPanel
                            bookingId={booking.id}
                            hasInvoice={Boolean(booking.invoice?.id)}
                            isCanceled={isCanceled}
                            // Gebühren ändern die Raten: Buchung und die Ratenliste der Zahlungskarte neu laden
                            refetchBooking={() => {
                                void refetch();
                                setTimeStamp(moment.unix(Date.now()).valueOf());
                            }}
                        />
                        <BookingTarif595Panel bookingId={booking.id} />
                        <BookingFeeLettersPanel bookingId={booking.id} hasInvoice={Boolean(booking.invoice?.id)} scheduledPayments={booking.scheduledPayments} />
                        <PanelCardNotes booking={booking} />
                        <PanelCard
                            title={<InvoiceHeader />}
                            columns={1}
                            gapAfter
                            extra={
                                <div style={{display: "flex", gap: 8}}>
                                    {invoiceData && !isCanceled && !booking.attendee?.erasedAt && (
                                        <CorrectBookingWizard
                                            bookingId={booking.id}
                                            invoiceId={invoiceData?.id}
                                            expectedGrossPrice={booking.grossPrice}
                                            currentBasePrice={booking.grossPrice + (booking.discount ?? 0) + (booking.memberDiscount ?? 0) + (booking.creditedBalance ?? 0)}
                                            currentCreditedBalance={booking.creditedBalance ?? 0}
                                            availableCredit={booking.attendee?.booker?.credit ?? 0}
                                        />
                                    )}
                                    {!isCanceled && <CorrectionInvoice bookingId={booking.id} invoiceId={invoiceData?.id} />}
                                </div>
                            }
                        >
                            {invoiceData === undefined ? <div>{"-"}</div> : <RenderInvoicePreview invoiceData={invoiceData} />}
                        </PanelCard>
                        <CommunicationHistoryBooking bookingId={booking.id} />
                    </div>
                ) : null}
            </>
        );
    };

    if (renderWithoutPage) {
        return renderContent();
    }

    return (
        <Page
            content={
                <PageContentWrapper>
                    {loading ? null : (
                        <LayoutStep
                            lessonBooking={course?.allowLessonBooking ?? false}
                            columns={1}
                            cancel={false}
                            style={{
                                border: isCanceled ? `2px solid ${red[3]}` : "none",
                                backgroundColor: isCanceled ? "#F5F5F5" : "unset",
                                ...modalStyle,
                            }}
                            title={`${booking?.bookingNumberPrefix}-${booking?.bookingNumber}`}
                            subtitle={
                                <span
                                    style={{
                                        color: isCanceled ? red[4] : "unset",
                                        fontWeight: isCanceled ? 500 : "unset",
                                    }}
                                >{`${i18n.containers.common.BookingTypes[bookingType]()} ${subTextSecondPart}`}</span>
                            }
                            icon={<Icon component={LeftMenuBookingIcon} />}
                            button={
                                <Space>
                                    <SendMailMenu booking={booking} />
                                    <BookingHistoryDrawer bookingId={bookingId} buttonProps={{size: "small", type: "link"}} />
                                    <MoreMenuSettlementDetails booking={booking} refetch={refetch} />
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => {
                                            if (setModalOpen) {
                                                setModalOpen(false);
                                            } else {
                                                returnBack();
                                            }
                                        }}
                                    >
                                        {i18n.containers.courses.CourseDetails.close()}
                                    </Button>
                                </Space>
                            }
                        >
                            <>{renderContent()}</>
                        </LayoutStep>
                    )}
                </PageContentWrapper>
            }
        />
    );
};

export default memo(SettledLessonBookingDetails);
